import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { API_ORIGIN } from "./api/config";
import { ApiProblem } from "./api/errors";
import { keys } from "./api/hooks";
import { createAppRouter } from "./router";
import { applyTheme, readTheme } from "./shell/theme";
import "./styles.css";

applyTheme(readTheme());

let router: ReturnType<typeof createAppRouter> | null = null;

/** A 401 anywhere means the session ended: clear cached data and go to login (returning afterwards). */
function onError(error: unknown) {
  if (error instanceof ApiProblem && error.status === 401 && router) {
    queryClient.setQueryData(keys.me, null);
    const here = router.state.location.pathname;
    if (here !== "/login") void router.navigate({ to: "/login", search: { next: here } });
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError }),
  mutationCache: new MutationCache({ onError }),
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      // Never retry authorization or not-found answers; retry transient failures once.
      retry: (count, error) => !(error instanceof ApiProblem && error.status >= 400 && error.status < 500) && count < 1,
    },
  },
});

router = createAppRouter(queryClient);

const container = document.getElementById("root");
if (!container) throw new Error("root element missing");

createRoot(container).render(
  <StrictMode>
    {API_ORIGIN === null ? (
      // A production build without VITE_API_ORIGIN: never guess a hostname.
      <main id="main" className="grid min-h-dvh place-items-center p-8 text-center text-sm">
        InteractAds no está configurado en este entorno (falta el origen de la API).
      </main>
    ) : (
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    )}
  </StrictMode>
);
